import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { SlidersHorizontal, X } from 'lucide-react'
import { Button, SelectPopover, SelectItem, ViewToggle } from 'components/ui'
import useLocaleText from 'components/useLocaleText'
import { RootState } from 'constants/interfaces'
import { isRtlSelector } from 'redux/selectors/general'
import { useDashboardReview } from '../../hooks/useDashboardReview'
import { filterReviewSubtasks, ReviewFilter as Filter } from '../../model/dashboardReview'
import { useWorkspaceText } from '../../hooks/useWorkspaceText'
import { useExecutionText } from '../../hooks/useExecutionText'
import { reviewLiterals } from './literals'
import styles from './styles.module.css'

export function ReviewFilter({ disabled }: { disabled: boolean }) {
  const review = useDashboardReview()!
  const t = useLocaleText(reviewLiterals)
  const rtl = useSelector(isRtlSelector)
  const workspace = useWorkspaceText()
  const all = useSelector((s: RootState) => s.tasks_v2.subtasks[review.task.tid] || [])
  const [mode, setMode] = useState<Filter['mode']>(review.filter.mode)
  const items: { id: string; label: string; detail?: string }[] =
    mode === 'subtask'
      ? [...all]
          .sort((a, b) => a.order_index - b.order_index)
          .map((st) => ({ id: st.st_id, label: st.title, detail: undefined }))
      : Array.from(new Set(all.map((st) => st.category || '').filter(Boolean))).map((id) => ({
          id,
          label: id,
          detail:
            all.filter((st) => (st.category || '') === id).length === 1
              ? workspace.oneSubtask
              : workspace.subtaskCount.replace(
                  '{count}',
                  String(all.filter((st) => (st.category || '') === id).length)
                ),
        }))
  return (
    <SelectPopover
      items={items}
      getItemId={(item) => item.id}
      renderItem={(item, selected) => (
        <SelectItem
          label={item.label}
          trailingElement={item.detail}
          indicatorPosition="start"
          isSelected={selected}
          mode="single"
          isRtl={rtl}
        />
      )}
      selectedIds={review.filter.ids}
      onSubmit={(ids) => review.setFilter({ mode, ids })}
      onOpen={() => setMode(review.filter.mode)}
      renderControls={(_, setIds) => (
        <ViewToggle
          fullWidth
          value={mode}
          onChange={(next) => {
            setMode(next)
            setIds([])
          }}
          options={[
            { id: 'subtask', icon: <span className={styles.toggleLabel}>{t.bySubtask}</span>, ariaLabel: t.bySubtask },
            {
              id: 'category',
              icon: <span className={styles.toggleLabel}>{t.byCategory}</span>,
              ariaLabel: t.byCategory,
            },
          ]}
        />
      )}
      trigger={
        <Button
          variant="primary"
          isDisabled={disabled}
          borderRadius="8px"
          h="36px"
          fontSize="13px"
          px="14px"
          startIcon={<SlidersHorizontal size={14} />}
        >
          {t.filter}
          {!!review.filter.ids.length && <span className={styles.count}>{review.filter.ids.length}</span>}
        </Button>
      }
      appearance="compact"
      usePortal={false}
      placement={rtl ? 'bottom-start' : 'bottom-end'}
      isRtl={rtl}
      menuWidth={370}
      filterLabel={t.filter}
      clearLabel={t.clear}
      emptyLabel={t.empty}
      description={t.hint}
      submitLabel={(ids) => {
        const count = filterReviewSubtasks(all, { mode, ids }).length
        return `${t.show} ${count} ${t.subtasks}`
      }}
    />
  )
}

export function ReviewFilterBadges({
  removable = false,
  disabled = false,
}: {
  removable?: boolean
  disabled?: boolean
}) {
  const review = useDashboardReview()
  const t = useLocaleText(reviewLiterals)
  const execution = useExecutionText()
  const all = useSelector((s: RootState) => (review ? s.tasks_v2.subtasks[review.task.tid] || [] : []))
  if (!review?.filter.ids.length) return null
  return (
    <div className={styles.badges}>
      {review.filter.ids.map((id) => (
        <span className={styles.badge} key={id}>
          {review.filter.mode === 'category' ? t.category : execution.subtask}:{' '}
          <strong>{review.filter.mode === 'category' ? id : all.find((st) => st.st_id === id)?.title || id}</strong>
          {removable && (
            <Button
              variant="icon"
              h="22px"
              minW="22px"
              p={0}
              isDisabled={disabled}
              aria-label={`${execution.remove}: ${
                review.filter.mode === 'category' ? id : all.find((st) => st.st_id === id)?.title || id
              }`}
              onClick={() =>
                review.setFilter({ ...review.filter, ids: review.filter.ids.filter((value) => value !== id) })
              }
            >
              <X size={12} />
            </Button>
          )}
        </span>
      ))}
    </div>
  )
}
